import { useMemo } from 'react';
import { ensureLoSiteBlockIds } from '@/lib/lyrraone-site-blocks';
import { LoSiteBlockView } from '@/components/lyrraone/LoSiteBlockView';
import { getLoSiteThemeRootClass } from '@/lib/lyrraone-site-themes';
import { getLoSiteThemeProfile, getLoSiteThemeProfileStyle } from '@/lib/lyrraone-site-theme-profiles';
import { cn } from '@/lib/utils';
import '@/styles/lyrraone-landing.css';

export type LoSiteTemplatePreviewMeta = {
  accent: string;
  accentSoft: string;
  heroTone: 'light' | 'dark' | 'gradient';
  thumbnailUrl?: string;
};

interface LoSiteTemplateThumbnailProps {
  homePreview: Array<Record<string, unknown>>;
  preview: LoSiteTemplatePreviewMeta;
  name: string;
  siteTheme?: string | null;
  compact?: boolean;
}

function LoSiteTemplateMockHeader({
  name,
  dark,
}: {
  name: string;
  dark?: boolean;
}) {
  return (
    <header
      className={cn(
        'lo-site-template-thumb__mock-header',
        dark && 'lo-site-template-thumb__mock-header--dark',
      )}
      aria-hidden
    >
      <span className="lo-site-template-thumb__mock-logo">{name.slice(0, 1)}</span>
      <span className="lo-site-template-thumb__mock-name">{name}</span>
      <nav className="lo-site-template-thumb__mock-nav">
        <span>Accueil</span>
        <span>Formations</span>
        <span>Contact</span>
      </nav>
    </header>
  );
}

export function LoSiteTemplateThumbnail({
  homePreview,
  preview,
  name,
  siteTheme,
  compact = false,
}: LoSiteTemplateThumbnailProps) {
  const blocks = useMemo(() => ensureLoSiteBlockIds(homePreview), [homePreview]);
  const themeRootClass = getLoSiteThemeRootClass(siteTheme);
  const profile = getLoSiteThemeProfile(siteTheme);
  const isDark = preview.heroTone === 'dark' || profile.header === 'dark';

  const canvasBg = preview.heroTone === 'dark'
    ? '#0f172a'
    : preview.heroTone === 'gradient'
      ? `linear-gradient(165deg, ${preview.accentSoft} 0%, #fff 45%)`
      : '#fff';

  if (preview.thumbnailUrl) {
    return (
      <div
        className={
          compact
            ? 'lo-site-template-thumb lo-site-template-thumb--compact'
            : 'lo-site-template-thumb'
        }
        aria-hidden
      >
        <div className="lo-site-template-thumb__chrome">
          <span className="lo-site-template-thumb__dot" />
          <span className="lo-site-template-thumb__dot" />
          <span className="lo-site-template-thumb__dot" />
          <span className="lo-site-template-thumb__url">{name}</span>
        </div>
        <div className="lo-site-template-thumb__viewport lo-site-template-thumb__viewport--photo">
          <LoSiteTemplateMockHeader name={name} dark={isDark} />
          <img
            src={preview.thumbnailUrl}
            alt=""
            className="lo-site-template-thumb__photo"
            loading="lazy"
            decoding="async"
          />
          <div
            className="lo-site-template-thumb__photo-overlay"
            style={{
              background: isDark
                ? 'linear-gradient(to bottom, rgba(15,23,42,0.35) 0%, rgba(15,23,42,0.1) 40%, transparent 70%)'
                : 'linear-gradient(to bottom, rgba(255,255,255,0.5) 0%, transparent 35%)',
            }}
          />
          <div className="lo-site-template-thumb__photo-hero">
            <span
              className="lo-site-template-thumb__photo-cta"
              style={{ backgroundColor: preview.accent }}
            >
              Commencer
            </span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={
        compact
          ? 'lo-site-template-thumb lo-site-template-thumb--compact'
          : 'lo-site-template-thumb'
      }
      aria-hidden
    >
      <div className="lo-site-template-thumb__chrome">
        <span className="lo-site-template-thumb__dot" />
        <span className="lo-site-template-thumb__dot" />
        <span className="lo-site-template-thumb__dot" />
        <span className="lo-site-template-thumb__url">{name}</span>
      </div>
      <div
        className="lo-site-template-thumb__viewport"
        style={{ background: canvasBg }}
      >
        <div
          className="lo-site-template-thumb__accent-bar"
          style={{ backgroundColor: preview.accent }}
        />
        <div
          className={cn('lo-site-template-thumb__scaled lyrraone-landing', themeRootClass)}
          style={getLoSiteThemeProfileStyle(siteTheme)}
        >
          <LoSiteTemplateMockHeader name={name} dark={isDark} />
          {blocks.map((block, i) => (
            <LoSiteBlockView
              key={block.id ?? i}
              block={block}
              blockKey={block.id ?? String(i)}
              formations={[]}
              siteSlug="preview"
              siteTheme={siteTheme}
            />
          ))}
        </div>
        <div className="lo-site-template-thumb__fade" />
      </div>
    </div>
  );
}
